/* eslint-disable jsx-a11y/alt-text */
import { css } from '@emotion/core';
import { useState } from 'react';
import t from 'react-translate';
import { UploadedFile } from 'redux/schemas/models/file-uploads';
import NvVideoPreview from 'shared/components/nv-video-preview';
import { gray1, gray4, hexToRgbaString } from 'styles/global_defaults/colors';
import { halfSpacing, standardSpacing } from 'styles/global_defaults/scaffolding';
import prodPathReplace from '../prod-path-rewrite';
import { config } from '../../../config/config.json';


const normalizeFileType = (fileType, fileName) => {
  fileType = fileType.split(';codecs')[0];
  if (!fileType) {
    const extension = `.${fileName.split('.').reverse()[0]}`;
    fileType = config.files.extensionToMimeType[extension];
  }
  if (config.files.images.split(',').includes(fileType)) {
    return 'image';
  } if (config.files.videos.split(',').includes(fileType)) {
    return 'video';
  } if (config.files.audios.split(',').includes(fileType)) {
    return 'audio';
  } if (config.files.pdfs.split(',').includes(fileType)) {
    return 'pdf';
  } if (config.files.documents.split(',').includes(fileType)) {
    return 'document';
  } if (config.files.excels.split(',').includes(fileType)) {
    return 'spreadsheet';
  } if (config.files.presentations.split(',').includes(fileType)) {
    return 'presentation';
  } if (config.files.zips.split(',').includes(fileType)) {
    return 'zip';
  }
  return 'other';
};

const getImageSrc = (fileType) => {
  // NOTE: have to return the full path string so gulp will be able to process properly
  switch (fileType) {
    case 'pdf':
      return prodPathReplace('images/file-pdf.png');
    case 'image':
      return prodPathReplace('images/file-image.png');
    case 'video':
      return prodPathReplace('images/file-video.png');
    case 'audio':
      return prodPathReplace('images/file-audio.png');
    case 'document':
      return prodPathReplace('images/file-doc.png');
    case 'spreadsheet':
      return prodPathReplace('images/file-excel.png');
    case 'presentation':
      return prodPathReplace('images/file-powerpoint.png');
    case 'externaldocument':
      return prodPathReplace('images/file-externaltools.png');
    case 'zip':
      return prodPathReplace('images/file-zip.png');
    case 'other':
      return prodPathReplace('images/file-other.png');
    default:
      return null;
  }
};

type NvUploadedFileProps = {
  file: UploadedFile,
  // editable?: any,
  displayInline?: any,
  // displayInlineStrict?: any,
  // displayNameOnly?: any,
  // downloadAlwaysVisible?: any,
  onDelete?: any,
  // clickHandler?: any,
  originalUrl?: any,
  showConfirmation?: any,
  displayNameOnly?: any;
};

const NvUploadedFile = ({
  file,
  displayInline,
  displayNameOnly,
  onDelete,
}: NvUploadedFileProps) => {
  const styles = css`
    .file-container {
      position: relative;

      .deletion-confirmation {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: ${hexToRgbaString(gray1, 0.95)};
        color: white;
        text-align: center;
        display: flex;
        justify-content: center;
        align-items: center;

        .deletion-confirmation-content {
          ${displayNameOnly && css`
            display: flex;
          `}

          .confirmation-text {
            margin-bottom: ${halfSpacing}px;

            ${displayNameOnly && css`
              margin-right: ${standardSpacing}px;
              margin-bottom: 0;
              display: flex;
              align-items: center;
            `}
          }
        }

        .btn-danger {
          margin-left: ${halfSpacing}px;
        }
      }
    }

    &:hover {
      .file-container {
        border: 1px dashed ${gray4};
      }
    }

    &.confirming-delete {
      .remove-file-section .icon-trash {
        visibility: hidden;
      }

      .file-container {
        border-color: transparent;
      }
    }

    .icon-trash {
      cursor: pointer;
      visibility: hidden;
    }

    &:hover {
      .icon-trash {
        visibility: visible;
      }
    }

    img {
      height: 60px;
    }
  `;


  const getContent = (fileType) => {
    switch (fileType) {
      case 'video':
        return <NvVideoPreview file={file} />;
      case 'pdf':
      case 'image':
      case 'audio':
      case 'document':
      case 'spreadsheet':
      case 'presentation':
      case 'externaldocument':
      case 'zip':
      case 'other':
      default:
        return null;
    }
  };

  const [showConfirmation, setShowConfirmation] = useState(false);
  const normalizedFileType = normalizeFileType(file.type, file.name);

  return (
    <div className={showConfirmation ? 'confirming-delete' : ''} css={styles}>
      {onDelete && (
        <div className='remove-file-section pb-4'>
          <div className='icon icon-smallest icon-trash float-right' onClick={() => setShowConfirmation(true)} />
        </div>
      )}
      <div className='file-container'>
        {displayNameOnly ? (
          <div className='bg-gray-7'>
            <img src={getImageSrc(normalizedFileType)} />
            <span className='file-name text-small pl-4'>{file.name}</span>
          </div>
        ) : getContent(normalizedFileType)}
        {showConfirmation && (
          <div className='deletion-confirmation'>
            <div className='deletion-confirmation-content'>
              <div className='confirmation-text'>{t.FILE_UPLOAD.ARE_YOU_SURE()}</div>
              <button type='button' className='btn btn-default btn-sm dark-theme' onClick={() => setShowConfirmation(false)}>{t.FORM.CANCEL()}</button>
              <button type='button' className='btn btn-danger btn-sm dark-theme' onClick={onDelete}>{t.FORM.DELETE()}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NvUploadedFile;
